import React, { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import logo from "../assets/logo.png";

export default function AdminLayout() {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      {/* NAVBAR ADMIN */}
      <header
        className={`sticky top-0 z-40 w-full border-b ${
          theme === "dark"
            ? "border-slate-800 bg-slate-900/80"
            : "border-slate-200 bg-white/80"
        } backdrop-blur`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
          {/* LOGO */}
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="Moroccan Desert Trek" className="h-8 w-10" />
            <span
              className={`text-lg font-bold ${
                theme === "dark" ? "text-slate-100" : "text-slate-800"
              }`}
            >
              Moroccan Desert Trek
            </span>
          </Link>

          {/* MENU BURGER MOBILE */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="block md:hidden rounded-md border border-slate-300 p-2 text-xl dark:border-slate-700"
          >
            {menuOpen ? "✖" : "☰"}
          </button>

          {/* NAVBAR RIGHT */}
          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/admin"
              className="text-lg font-bold flex items-center gap-2 hover:text-emerald-600 transition"
            >
              <span>🔧</span>
              <p>Admin Panel</p>
            </Link>

            <button
              onClick={toggleTheme}
              className={`rounded-md border px-2 py-1 text-xs ${
                theme === "dark"
                  ? "border-slate-700 text-slate-200 hover:bg-slate-800"
                  : "border-slate-200 text-slate-700 hover:bg-slate-50"
              }`}
            >
              {theme === "dark" ? "☀️ Light" : "🌙 Dark"}
            </button>

            <button
              onClick={logout}
              className={`rounded-md px-3 py-1.5 text-sm ${
                theme === "dark"
                  ? "bg-slate-100 text-slate-900 hover:bg-white"
                  : "bg-slate-900 text-white hover:bg-slate-700"
              }`}
            >
              Logout
            </button>
          </div>
        </div>

        {/* MENU MOBILE DROPDOWN */}
        {menuOpen && (
          <div className="md:hidden border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            <nav className="flex flex-col px-4 py-2 space-y-2">
              <NavLink
                to="/admin"
                end
                className="py-2 hover:text-emerald-600"
                onClick={() => setMenuOpen(false)}
              >
                📊 Dashboard
              </NavLink>
              <NavLink
                to="/admin/tours"
                className="py-2 hover:text-emerald-600"
                onClick={() => setMenuOpen(false)}
              >
                🗺️ Tours
              </NavLink>
              <NavLink
                to="/admin/users"
                className="py-2 hover:text-emerald-600"
                onClick={() => setMenuOpen(false)}
              >
                👥 Users
              </NavLink>
              <NavLink
                to="/admin/bookings"
                className="py-2 hover:text-emerald-600"
                onClick={() => setMenuOpen(false)}
              >
                📅 Bookings
              </NavLink>
              <NavLink
                to="/admin/reviews"
                className="py-2 hover:text-emerald-600"
                onClick={() => setMenuOpen(false)}
              >
                ⭐ Reviews
              </NavLink>
              <NavLink
                to="/admin/messages"
                className="py-2 hover:text-emerald-600"
                onClick={() => setMenuOpen(false)}
              >
                📩 Messages
              </NavLink>
              <NavLink
                to="/admin/settings"
                className="py-2 hover:text-emerald-600"
                onClick={() => setMenuOpen(false)}
              >
                ⚙️ Settings
              </NavLink>
              <div className="flex justify-between pt-2 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={toggleTheme}
                  className="text-xs text-slate-600 dark:text-slate-300"
                >
                  {theme === "dark" ? "☀️ Light" : "🌙 Dark"}
                </button>
                <button
                  onClick={logout}
                  className="text-xs text-red-500 hover:text-red-600"
                >
                  Logout
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* CONTENU ADMIN */}
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-8 md:grid-cols-4">
        {/* SIDEBAR (seulement sur desktop) */}
        <aside className="hidden md:block space-y-3 rounded-xl border border-slate-200 p-4 dark:border-slate-800 md:col-span-1">
          <nav className="flex flex-col gap-2 text-sm">
            {[
              { to: "/admin", label: "📊 Dashboard" },
              { to: "/admin/tours", label: "🗺️ Tours" },
              { to: "/admin/users", label: "👥 Users" },
              { to: "/admin/bookings", label: "📅 Bookings" },
              { to: "/admin/reviews", label: "⭐ Reviews" },
              { to: "/admin/messages", label: "📩 Messages" },
              { to: "/admin/settings", label: "⚙️ Settings" },
            ].map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/admin"}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 ${
                    isActive
                      ? "bg-emerald-600 text-white"
                      : "hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* CONTENU PRINCIPAL */}
        <main className="md:col-span-3">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
